import { ChangeDetectionStrategy, Component, type OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CloudApiService, type NamespaceMember } from '@/cloud/services/cloud-api.service';
import { UserAvatarComponent } from '@/shared/user-avatar/user-avatar.component';
import { RoleBadgeComponent } from '@/shared/role-badge/role-badge.component';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';

/** Who has access to the tenant and with which role; add existing users or invite by email. */
@Component({
    selector: 'app-namespace-members',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DialogModule, InputTextModule, SelectModule, TableModule, TagModule, ConfirmDialogModule, TooltipModule, UserAvatarComponent, RoleBadgeComponent, SectionCardComponent, EmptyStateComponent],
    providers: [ConfirmationService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './namespace-section.scss',
    template: `
        <p-confirmdialog />
        <app-section-card title="Members" subtitle="Who has access to this tenant and with which role." icon="pi pi-users" [count]="members().length" [flush]="true">
            <p-button actions label="Add member" icon="pi pi-user-plus" (onClick)="openAdd()" />

            <p-table class="management-table" [value]="members()" [loading]="loading()" [scrollable]="true" dataKey="userId">
                <ng-template #header>
                    <tr>
                        <th>Member</th>
                        <th class="hidden md:table-cell">Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th class="text-right"><span class="sr-only">Actions</span></th>
                    </tr>
                </ng-template>
                <ng-template #body let-member>
                    <tr>
                        <td>
                            <div class="ns-identity">
                                <app-user-avatar [user]="member" />
                                <div>
                                    <strong>{{ member.displayName }}</strong>
                                    <small class="text-color-secondary">{{ member.username }}</small>
                                </div>
                            </div>
                        </td>
                        <td class="hidden md:table-cell">{{ member.email || '—' }}</td>
                        <td>
                            @if (canEdit(member)) {
                                <p-select
                                    [options]="roleOptions()"
                                    optionLabel="name"
                                    optionValue="id"
                                    [ngModel]="member.roleId"
                                    (ngModelChange)="changeRole(member, $event)"
                                    [disabled]="savingRole() === member.userId"
                                    [ariaLabel]="'Role of ' + member.displayName"
                                    size="small"
                                    appendTo="body"
                                />
                            } @else {
                                <app-role-badge [roleId]="member.roleId" [roleName]="member.roleName" />
                            }
                        </td>
                        <td>
                            @if (member.pendingInvite) {
                                <p-tag value="Invited" severity="warn" [rounded]="true" />
                            } @else if (member.disabled) {
                                <p-tag value="Disabled" severity="danger" [rounded]="true" />
                            } @else {
                                <p-tag value="Active" severity="success" [rounded]="true" />
                            }
                        </td>
                        <td>
                            <div class="management-row-actions">
                                @if (member.pendingInvite) {
                                    <p-button icon="pi pi-envelope" [text]="true" [rounded]="true" pTooltip="Resend invite" tooltipPosition="left" [ariaLabel]="'Resend invite to ' + member.displayName" (onClick)="resendInvite(member)" />
                                }
                                <p-button icon="pi pi-trash" severity="danger" [text]="true" [rounded]="true" pTooltip="Remove access" tooltipPosition="left" [ariaLabel]="'Remove access for ' + member.displayName" (onClick)="remove(member)" />
                            </div>
                        </td>
                    </tr>
                </ng-template>
                <ng-template #emptymessage>
                    <tr>
                        <td colspan="5">
                            <app-empty-state icon="pi pi-users" title="No members yet" description="Add one to grant access." [compact]="true" />
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </app-section-card>

        <p-dialog header="Add member" [visible]="showAdd()" [modal]="true" [closable]="true" (visibleChange)="showAdd.set($event)" [style]="{ width: '32rem' }" appendTo="body">
            <div class="ns-dialog-body">
                <div class="ns-field">
                    <label for="member-user">Existing user</label>
                    <p-select
                        inputId="member-user"
                        [options]="addableUsers()"
                        optionLabel="label"
                        optionValue="id"
                        [ngModel]="selectedUserId()"
                        (ngModelChange)="selectedUserId.set($event)"
                        placeholder="Select a user"
                        [showClear]="true"
                        [filter]="true"
                        class="w-full"
                        appendTo="body"
                    />
                </div>

                <div class="ns-divider">or invite by email</div>

                <div class="ns-field">
                    <label for="member-email">Email</label>
                    <input id="member-email" pInputText type="email" [(ngModel)]="inviteEmail" placeholder="person@company.com" class="w-full" [disabled]="!!selectedUserId()" />
                </div>

                <div class="ns-field">
                    <label for="member-role">Role</label>
                    <p-select inputId="member-role" [options]="roleOptions()" optionLabel="name" optionValue="id" [ngModel]="addRoleId()" (ngModelChange)="addRoleId.set($event)" class="w-full" appendTo="body" />
                </div>
            </div>
            <ng-template #footer>
                <p-button label="Cancel" [text]="true" (onClick)="showAdd.set(false)" [disabled]="adding()" />
                <p-button [label]="selectedUserId() ? 'Add' : 'Send invite'" [icon]="selectedUserId() ? 'pi pi-user-plus' : 'pi pi-send'" (onClick)="add()" [loading]="adding()" [disabled]="adding() || (!selectedUserId() && !inviteEmail().trim())" />
            </ng-template>
        </p-dialog>
    `
})
export class NamespaceMembers implements OnInit {
    private api = inject(CloudApiService);
    private route = inject(ActivatedRoute);
    private confirmationService = inject(ConfirmationService);
    private messageService = inject(MessageService);

    private namespaceId = signal('');
    members = signal<NamespaceMember[]>([]);
    loading = signal(false);
    savingRole = signal<string | null>(null);

    showAdd = signal(false);
    adding = signal(false);
    selectedUserId = signal<string | null>(null);
    inviteEmail = signal('');
    addRoleId = signal('reader');
    private allUsers = signal<any[]>([]);

    roleOptions = computed(() => (this.api.roles() ?? []).map((r) => ({ id: r.id, name: r.name })));

    addableUsers = computed(() => {
        const memberIds = new Set(this.members().map((m) => m.userId));
        return this.allUsers()
            .filter((u) => !memberIds.has(u.id))
            .map((u) => ({ id: u.id, label: `${u.displayName || u.username} (${u.email || u.username})` }));
    });

    ngOnInit(): void {
        this.namespaceId.set(this.route.parent?.snapshot.paramMap.get('namespaceId') ?? '');
        this.api.getRoles().catch(() => undefined);
        this.refresh();
    }

    refresh() {
        if (!this.namespaceId()) return;
        this.loading.set(true);
        this.api
            .getNamespaceMembers(this.namespaceId())
            .then((members) => this.members.set(members))
            .catch((err) => this.toastError('Failed to load members', err))
            .finally(() => this.loading.set(false));
    }

    // Members whose own role can't be edited: the current user (backend forbids
    // self-change) — the control stays visible for everyone else.
    canEdit(member: NamespaceMember): boolean {
        return member.userId !== this.api.currentUser()?.id;
    }

    changeRole(member: NamespaceMember, roleId: string) {
        if (!roleId || roleId === member.roleId) return;
        this.savingRole.set(member.userId);
        this.api
            .grantPermission(member.userId, this.namespaceId(), this.legacyTier(roleId), roleId)
            .then(() => {
                this.messageService.add({ severity: 'success', summary: 'Role Updated', detail: `${member.displayName} is now ${this.roleName(roleId)}` });
                this.refresh();
            })
            .catch((err) => {
                this.toastError('Role Change Failed', err);
                this.refresh();
            })
            .finally(() => this.savingRole.set(null));
    }

    remove(member: NamespaceMember) {
        this.confirmationService.confirm({
            header: 'Remove Access',
            message: `Remove ${member.displayName}'s access to this tenant?`,
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            acceptLabel: 'Remove',
            accept: () => {
                this.api
                    .revokePermission(this.namespaceId(), member.userId)
                    .then(() => {
                        this.messageService.add({ severity: 'success', summary: 'Access Removed', detail: member.displayName });
                        this.refresh();
                    })
                    .catch((err) => this.toastError('Remove Failed', err));
            }
        });
    }

    resendInvite(member: NamespaceMember) {
        this.api
            .resendInvite(member.userId)
            .then((res) => this.messageService.add({ severity: 'success', summary: 'Invite Sent', detail: res.email }))
            .catch((err) => this.toastError('Resend Failed', err));
    }

    openAdd() {
        this.selectedUserId.set(null);
        this.inviteEmail.set('');
        this.addRoleId.set('reader');
        this.showAdd.set(true);
        this.api
            .getUsers()
            .then((users: any[]) => this.allUsers.set(users ?? []))
            .catch(() => this.allUsers.set([]));
    }

    add() {
        const roleId = this.addRoleId();
        this.adding.set(true);
        const done = (summary: string, detail: string) => {
            this.messageService.add({ severity: 'success', summary, detail });
            this.showAdd.set(false);
            this.refresh();
        };
        if (this.selectedUserId()) {
            this.api
                .grantPermission(this.selectedUserId()!, this.namespaceId(), this.legacyTier(roleId), roleId)
                .then(() => done('Member Added', this.roleName(roleId)))
                .catch((err) => this.toastError('Add Failed', err))
                .finally(() => this.adding.set(false));
        } else {
            const email = this.inviteEmail().trim();
            this.api
                .inviteUser(email, [{ appNamespaceId: this.namespaceId(), role: this.legacyTier(roleId), roleId }])
                .then(() => done('Invite Sent', email))
                .catch((err) => this.toastError('Invite Failed', err))
                .finally(() => this.adding.set(false));
        }
    }

    private roleName(roleId: string): string {
        return this.roleOptions().find((r) => r.id === roleId)?.name ?? roleId;
    }

    private legacyTier(roleId: string): number {
        return { reader: 0, writer: 1, admin: 2 }[roleId.toLowerCase()] ?? 0;
    }

    private toastError(summary: string, err: any) {
        const detail = err?.error?.message ?? (typeof err?.error === 'string' ? err.error : null) ?? err?.message ?? 'An error occurred';
        this.messageService.add({ severity: 'error', summary, detail });
    }
}
